import { useCallback, useState } from "react";
import { useObjectUiAdapter } from "@shared-core/data/ObjectUiAdapterContext";
import type {
  ChildRow,
  ManifestList,
  ManifestListFilter,
} from "@shared-core/domain/object-ui/contract";
import {
  markedName,
  type EntityFilterTarget,
} from "@shared-core/domain/object-ui/entity-filter";
import { useEntityFilterName } from "@shared-core/domain/object-ui/hooks";
import { emptyListQuery } from "@shared-core/domain/object-ui/list-query";
import {
  EntityCombobox,
  type EntityOption,
} from "../../app/components/patterns/EntityCombobox";
import { optionOf } from "../../app/components/patterns/command-shell/entity-option";

/**
 * A list filter that names another object's record (R5): the command dialog's name picker, searching the named
 * object's own list, whose choice the list is filtered by. The box shows the record's name: the one picked, or for a
 * value that came in a link, the name the rows or a search of the named list give (else the value itself).
 *
 * The picker searches the named list as the list declares a filter's picker should (`picker_value`): the Owner filter
 * finds a user who has left, marked "(left)", so history can be filtered by them.
 */
export function EntityFilter({
  list,
  filter,
  target,
  value,
  rows,
  className,
  onChange,
}: {
  /** The list being filtered (its rows may name the chosen record). */
  list: ManifestList;
  filter: ManifestListFilter;
  target: EntityFilterTarget;
  value: string | null;
  /** The filtered list's rows, once loaded. */
  rows: readonly ChildRow[] | null;
  className?: string;
  onChange: (value: string | null) => void;
}) {
  const adapter = useObjectUiAdapter();
  const [picked, setPicked] = useState<EntityOption | null>(null);
  const resolved = useEntityFilterName(
    list,
    target,
    picked?.id === value ? null : value,
    rows,
  );
  const current: EntityOption | null = value
    ? picked?.id === value
      ? picked
      : { id: value, label: resolved ?? value }
    : null;

  const filtersKey = JSON.stringify(target.filters);
  const fetchPage = useCallback(
    async (q: string, page: number) => {
      const result = await adapter.list(target.list, {
        ...emptyListQuery(),
        q: q || null,
        filters: target.filters,
        page,
        page_size: 20,
      });
      const options = result.items
        .map((item) => {
          const option = optionOf(target.object, item, target.valueField);
          return option.label === option.id
            ? option
            : { ...option, label: markedName(target, item, option.label) };
        })
        // A row with no value to filter by (a customer with no Tharstern code) can't be chosen; one record is one choice.
        .filter((option) => option.id !== "");
      return {
        options: options.filter(
          (option, i) => options.findIndex((o) => o.id === option.id) === i,
        ),
        hasMore: result.page < result.total_pages,
      };
    },
    // The target is rebuilt each render from the manifest's own (stable) objects: depend on those, not on it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adapter, target.list, target.object, target.valueField, filtersKey],
  );

  return (
    <EntityCombobox
      value={current}
      ariaLabel={filter.label}
      placeholder={`${filter.label}: any`}
      searchPlaceholder={`Find ${filter.label.toLowerCase()}…`}
      className={className}
      fetchPage={fetchPage}
      onChange={(option) => {
        // The box shows the record's name: an option offered by its number carries the name as its hint.
        setPicked(
          option && option.label === option.id && option.hint
            ? { id: option.id, label: option.hint }
            : option,
        );
        onChange(option?.id ?? null);
      }}
    />
  );
}
